import { ref } from 'vue'

export interface SortParam {
  sortBy: string
  descending: boolean
}

export interface PaginationState {
  page: number
  rowsPerPage: number
  rowsNumber: number
  sortBy?: string | SortParam[]
  descending?: boolean
}

export interface RestDataConfig {
  initialPageSize?: number
  defaultSort?: SortParam[]
}

export function useRestData<T>(
  apiFetcher: (params: any, initOverrides?: RequestInit) => Promise<any>,
  config: RestDataConfig = {}
) {
  const items = ref<T[]>([]) as import('vue').Ref<T[]>
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const searchQuery = ref('')
  const activeFilters = ref<Record<string, any>>({})

  const initialPagination: PaginationState = {
    page: 1,
    rowsPerPage: config.initialPageSize ?? 20,
    rowsNumber: 0
  }

  if (config.defaultSort && config.defaultSort.length > 0) {
    if (config.defaultSort.length === 1) {
      initialPagination.sortBy = config.defaultSort[0].sortBy
      initialPagination.descending = config.defaultSort[0].descending
    } else {
      initialPagination.sortBy = config.defaultSort
    }
  }

  const pagination = ref<PaginationState>(initialPagination)
  let abortController: AbortController | null = null

  const fetch = async (additionalParams: Record<string, any> = {}) => {
    if (abortController) abortController.abort()
    abortController = new AbortController()

    isLoading.value = true
    error.value = null

    try {
      let sortParam: string | undefined = undefined
      if (Array.isArray(pagination.value.sortBy)) {
        sortParam = pagination.value.sortBy
          .map((s: SortParam) => `${s.descending ? '-' : '+'}${s.sortBy}`)
          .join(',')
      } else if (pagination.value.sortBy) {
        sortParam = `${pagination.value.descending ? '-' : '+'}${pagination.value.sortBy}`
      }

      // Enhanced Filter Mapping
      const filterArray = Object.entries(activeFilters.value)
        .map(([k, val]) => {
          // Handle { v, op } objects
          if (val !== null && typeof val === 'object' && 'v' in val) {
            return { k, v: val.v, op: val.op || 'eq' }
          }
          // Default to primitive with 'eq'
          return { k, v: val, op: 'eq' }
        })
        .filter((f) => f.v !== null && f.v !== '' && f.v !== undefined)

      const filtersParam = filterArray.length > 0 ? JSON.stringify(filterArray) : undefined

      const queryParams = {
        page: pagination.value.page,
        pageSize: pagination.value.rowsPerPage,
        ...(searchQuery.value ? { search: searchQuery.value } : {}),
        ...(sortParam ? { sort: sortParam } : {}),
        ...(filtersParam ? { filters: filtersParam } : {}),
        ...additionalParams
      }

      const response = await apiFetcher(queryParams, { signal: abortController.signal })
      items.value = response.items || []
      pagination.value.rowsNumber = response.totalCount || 0
    } catch (err: any) {
      if (err.name === 'AbortError') return
      error.value = err.message || 'An unexpected error occurred.'
      console.error('[useRestData Error]:', err)
    } finally {
      isLoading.value = false
    }
  }

  const onRequest = (props: { pagination: PaginationState; filter?: any }) => {
    pagination.value.page = props.pagination.page
    pagination.value.rowsPerPage = props.pagination.rowsPerPage
    pagination.value.sortBy = props.pagination.sortBy
    pagination.value.descending = props.pagination.descending
    if (props.filter !== undefined) searchQuery.value = props.filter
    fetch()
  }

  const applyFilters = () => {
    pagination.value.page = 1
    fetch()
  }
  const refresh = (params: Record<string, any> = {}) => {
    pagination.value.page = 1
    fetch(params)
  }

  return {
    items,
    isLoading,
    error,
    pagination,
    searchQuery,
    activeFilters,
    fetch,
    onRequest,
    applyFilters,
    refresh
  }
}
